import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertTriangle } from "lucide-react";

export function ConfirmDeleteModal({ open, onClose, onConfirm, title, message, itemName, requireTypeName = false }) {
  const [countdown, setCountdown] = useState(1);
  const [typedName, setTypedName] = useState("");
  const cancelRef = useRef(null);

  useEffect(() => {
    if (open) {
      setCountdown(1);
      setTypedName("");
      const timer = setTimeout(() => setCountdown(0), 1000);
      return () => clearTimeout(timer);
    }
  }, [open]);

  useEffect(() => {
    if (open && cancelRef.current) {
      cancelRef.current.focus();
    }
  }, [open]);

  const canConfirm = countdown === 0 && (!requireTypeName || typedName.trim().toLowerCase() === (itemName || "").trim().toLowerCase());

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md rounded-sm border border-gray-200" data-testid="confirm-delete-modal" style={{ zIndex: 60 }}>
        <DialogHeader>
          <DialogTitle className="font-heading text-lg font-semibold tracking-tight flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" strokeWidth={2} />
            {title || "Delete entry?"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-sm text-gray-600">{message || "This action cannot be undone."}</p>
          {requireTypeName && itemName && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Type <span className="font-semibold text-gray-700">"{itemName}"</span> to confirm:</p>
              <Input
                data-testid="confirm-type-name-input"
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                className="rounded-sm font-mono-data"
                placeholder={itemName}
                autoFocus={requireTypeName}
              />
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button
            ref={cancelRef}
            type="button"
            variant="outline"
            onClick={onClose}
            className="rounded-sm"
            data-testid="confirm-delete-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => { if (canConfirm) onConfirm(); }}
            disabled={!canConfirm}
            className="rounded-sm bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 relative overflow-hidden"
            data-testid="confirm-delete-btn"
          >
            {countdown > 0 && (
              <span className="absolute inset-0 bg-red-800" style={{ width: "100%", animation: "shrink-left 1s linear forwards" }} />
            )}
            <span className="relative uppercase text-xs font-semibold tracking-wider">
              {countdown > 0 ? "Wait..." : "Delete"}
            </span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
